import { useAuth } from '../../components/provider/OIDCProvider'
import { AdminUserPermission, useSchedulerLoginMutation } from '../../generated/graphql'
import { Roles } from '../../types'
import { userHasPermission, userHasRole, userIsAdmin } from '../authenticationUtils'

import useEnvironment from './useEnvironment'
import useNotification from './useNotification'

/**
 * Logs user into scheduler monolith.
 *
 * For Sample Student Login & civitas users, Student-API will store the required info in Redis,
 * to be fetched by oauth-server. Once the info is stored in redis, the client redirects to Scheduler,
 * including the login key as a query string to be passed along to oauth-server.
 *
 * For students or admins looking at their own plans, the Redis flow is bypassed,
 * and the user is simply redirected to the /login/student-app route in Scheduler.
 *
 * @param planOwnerAppUserId [optional] If provided, this will Sample Student Login
 * an admin user using the ID of the plan owner.
 * If not provided, the appUserId of the currently logged in user will be used.
 *
 * @param termCode [optional] If provided, Scheduler will automatically select the term when logging in.
 * If not provided, the user will be prompted by Scheduler to select a term.
 *
 * @returns a function to log a user into scheduler
 */
export default function useSchedulerLogin(
  planOwnerAppUserId?: number,
  termCode?: string,
  sourceEvent?: string
) {
  const schedulerMonolithUri = process.env.REACT_APP_SCHEDULER_MONOLITH_URI
  const schedulerMonolithHostUri = process.env.REACT_APP_SCHEDULER_HOST_URI || 'collegescheduler'
  const { showErrorNotif } = useNotification()
  const { user } = useAuth()
  const environment = useEnvironment()
  const baseUrl = schedulerMonolithUri || `https://${environment}.${schedulerMonolithHostUri}.com`

  const [schedulerLoginMutation] = useSchedulerLoginMutation({
    onCompleted: ({ schedulerLogin }) => {
      if (schedulerLogin?.key) {
        const sampleLoginSchedulerUrl = schedulerLogin?.key
          ? `${baseUrl}/index.aspx?loginKey=${schedulerLogin.key}${
              termCode ? `&termCode=${termCode}` : ''
            }`
          : ''
        window.location.assign(sampleLoginSchedulerUrl)
      }
    },
    onError: () => {
      showErrorNotif('Something went wrong. If this problem persists, please contact support.')
    }
  })

  const isAdmin = userIsAdmin(user)
  const isStudent = userHasRole(user, [Roles.student])
  const isPlanOwner = planOwnerAppUserId === user?.appUserId
  const isCivitasEmployee = userHasRole(user, [Roles.civitasEmployee])
  const hasSampleStudentLoginPermission = userHasPermission(user, [
    AdminUserPermission.SampleStudentLogin
  ])

  function doSchedulerLogin() {
    if (isCivitasEmployee && isPlanOwner) {
      schedulerLoginMutation({
        variables: {
          input: {
            // if no planOwnerAppUserId is provided, use current user's appUserId
            studentAppUserId: planOwnerAppUserId?.toString() || user?.appUserId?.toString()
          }
        }
      })
      return
    }

    // if is a student and the event originated from the navBar, then bail of mutation
    if (isStudent && sourceEvent === 'navBar') {
      window.location.assign(
        `${baseUrl}/login/student-app${termCode ? `?termCode=${termCode}` : ''}`
      )
      // bail out of calling mutation when redirecting to /login/student-app
      return
    }

    if ((isStudent && !isAdmin) || (isAdmin && isStudent && isPlanOwner)) {
      window.location.assign(
        `${baseUrl}/login/student-app${termCode ? `?termCode=${termCode}` : ''}`
      )
      // bail out of calling mutation when redirecting to /login/student-app
      return
    }

    if (isAdmin && hasSampleStudentLoginPermission) {
      schedulerLoginMutation({
        variables: {
          input: {
            // if no planOwnerAppUserId is provided, use current user's appUserId
            studentAppUserId: planOwnerAppUserId?.toString() || user?.appUserId?.toString()
          }
        }
      })
    }
  }

  return { doSchedulerLogin }
}
